import React, { useCallback, useEffect, useRef } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { useTerminalLogic } from './useTerminalLogic';
import 'xterm/css/xterm.css';
import { TERMINAL_SESSION_ID } from './terminalUtils';
import restApi from '../../services/restApi';

function TerminalComponent() {
  const terminalRef = useRef(null);
  const termInstance = useRef(null);
  const fitAddonRef = useRef(null);
  const optionsRef = useRef({
    theme: { background: '#1e1e1e', foreground: '#00ff00' },
    fontSize: 24,
    lineHeight: 1.2,
  });
  const logic = useTerminalLogic();

  // 状态同步 Ref，确保回调中拿到最新的 React State
  const stateRef = useRef(logic);
  useEffect(() => {
    stateRef.current = logic;
  }, [logic]);

  const init = useCallback(async (term, fitAddon) => {
    if (!sessionStorage.getItem(TERMINAL_SESSION_ID)) {
      const res = await restApi.get('/flow/api/flow-rest/terminal/session');
      if (res?.sessionId) {
        sessionStorage.setItem(TERMINAL_SESSION_ID, res?.sessionId);
      }
    }
    const res = await restApi.get('/flow/api/flow-rest/terminal/options');
    if (res?.options && term.options) {
      optionsRef.current = res.options;
      Object.entries(res.options).forEach(([key, value]) => {
        term.options[key] = value;
      });
      fitAddon.fit();
    }
  }, []);

  useEffect(() => {
    const term = new Terminal({
      cursorBlink: true,
      convertEol: true,
      ...optionsRef.current,
    });
    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(terminalRef.current);
    fitAddon.fit();
    term.write(logic.PROMPT);
    termInstance.current = term;
    fitAddonRef.current = fitAddon;

    init(term, fitAddon).catch((error) => {
      console.error(error);
    });

    // --- A. 处理系统级快捷键 (优先级最高) ---
    term.attachCustomKeyEventHandler((e) => {
      if (e.type !== 'keydown') return true;
      const { inputBuffer, history, historyIndex, isLoading, cursorPos, executeCommand } =
        stateRef.current;

      if (isLoading) return false;

      // 1. Ctrl + Enter: 发送命令
      if (e.ctrlKey && e.key === 'Enter') {
        executeCommand(term);
        return false;
      }

      // 2. Ctrl + S: 清屏
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        term.reset();
        term.write(stateRef.current.PROMPT);
        logic.setInputBuffer('');
        logic.setCursorPos(0);
        return false;
      }

      // 3. Ctrl + O: 显示历史
      if (e.ctrlKey && e.key === 'o') {
        term.write(
          `\r\n--- HISTORY ---\r\n${history.join('\r\n')}\r\n---------------\r\n\r\n${stateRef.current.PROMPT}${inputBuffer}`,
        );
        return false;
      }

      // 4. Alt + Up/Down: 切换历史
      if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        const isUp = e.key === 'ArrowUp';
        let idx = isUp ? historyIndex + 1 : historyIndex - 1;
        if (idx >= history.length) idx = history.length - 1;
        if (idx < -1) idx = -1;

        // 清除当前行
        for (let i = 0; i < inputBuffer.length; i++) term.write('\b \b');

        const cmd = idx === -1 ? '' : history[history.length - 1 - idx];
        term.write(cmd);
        logic.setInputBuffer(cmd);
        logic.setCursorPos(cmd.length);
        logic.setHistoryIndex(idx);
        return false;
      }

      // 5. 方向键处理 (由于我们要维护逻辑光标偏移)
      if (e.key === 'ArrowLeft') {
        if (cursorPos > 0) {
          logic.setCursorPos(cursorPos - 1);
          term.write('\x1b[D');
        }
        return false;
      }
      if (e.key === 'ArrowRight') {
        if (cursorPos < inputBuffer.length) {
          logic.setCursorPos(cursorPos + 1);
          term.write('\x1b[C');
        }
        return false;
      }

      // 6. Ctrl + H: 显示/隐藏帮助菜单
      if (e.ctrlKey && e.key === 'h') {
        e.preventDefault();
        logic.printHelp(term);
        return false;
      }

      return true; // 其他键放行给 onData
    });

    // --- B. 处理数据输入 (字符、退格、粘贴) ---
    term.onData((data) => {
      const { isLoading } = stateRef.current;
      if (isLoading) return;

      // --- 1. 处理特定的功能性控制字符 ---

      // 退格键 (ASCII 127 或 8)
      if (data === '\x7f' || data === '\b') {
        stateRef.current.handleBackspace(term);
        return;
      }

      // 回车键 (需求是 Ctrl+Enter 发送，所以普通回车直接屏蔽)
      if (data === '\r' || data === '\n') {
        return;
      }

      // --- 2. 过滤不可打印按键与转义序列 ---

      // 过滤 ESC 开头的序列 (如方向键、功能键 F1-F12 在 onData 中的表现)
      if (data.startsWith('\x1b')) {
        // 注意：方向键我们已经在 attachCustomKeyEventHandler 中处理了逻辑光标
        // 所以这里直接丢弃，防止它们作为字符写入 buffer
        return;
      }

      // 过滤其他单控制字符 (ASCII 0-31)
      // 排除掉我们可能需要的字符，剩下的全部拦截
      const charCode = data.charCodeAt(0);
      if (charCode < 32) {
        return;
      }

      // --- 3. 正常字符输入与粘贴 ---

      // 此时剩下的 data 才是真正的可打印文本（可能是单个字母，也可能是粘贴的一串大写字母）
      const upperData = data.toUpperCase();
      stateRef.current.handleInsert(upperData, term);
    });

    // 窗口自适应
    const resizeObserver = new ResizeObserver(() => fitAddon.fit());
    resizeObserver.observe(terminalRef.current);

    // 跨窗口交互监听
    const onMessage = (msg) => {
      if (typeof msg.data === 'string') {
        term.write(
          `\r\n${msg.data.toUpperCase()}\r\n${stateRef.current.PROMPT}${stateRef.current.inputBuffer}`,
        );
      } else if (msg.data && typeof msg.data === 'object' && !Array.isArray(msg.data)) {
        const { content, type, command } = msg.data;
        if (content) {
          // \r\n 换行，确保不覆盖当前 Prompt
          term.write(`\r\n${content.toUpperCase()}\r\n`);
        }

        if (type === 'command' && command) {
          term.write(`${command.toUpperCase()}\r\n`);
          logic.setInputBuffer(command.toUpperCase());
          logic.executeCommand(term, command.toUpperCase());
        } else {
          // 如果只是打印了 content，别忘了把提示符补回来
          term.write(`${stateRef.current.PROMPT}${stateRef.current.inputBuffer}`);
        }
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
      resizeObserver.disconnect();
      term.dispose();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ height: '100%', width: '100%', background: optionsRef.current.theme.background }}>
      <div ref={terminalRef} style={{ height: '100%' }} />
      {/* 这里的 Modal 组件需根据你的 UI 库自行替换 */}
      {logic.isModalOpen && (
        <div
          style={{
            position: 'absolute',
            top: '30%',
            left: '30%',
            padding: '20px',
            background: 'white',
            border: '1px solid #ccc',
            color: '#000',
          }}
        >
          <h3>SERVICE DIALOG</h3>
          <button onClick={() => logic.setIsModalOpen(false)}>CLOSE</button>
        </div>
      )}
    </div>
  );
}

export default TerminalComponent;
